import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MessageSquarePlus, Paperclip, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5MB, matches the server-side cap

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // FileReader's data URL includes a "data:<type>;base64," prefix --
      // only the part after the comma is the actual base64 payload.
      const result = reader.result as string;
      const base64 = result.split(",")[1] ?? "";
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function FeedbackWidget() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [wantsToShareEmail, setWantsToShareEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // On phones the floating button sits on top of form fields while someone
  // types, so it steps aside whenever a field outside the dialog has focus.
  const [fieldFocused, setFieldFocused] = useState(false);
  useEffect(() => {
    const isField = (t: EventTarget | null) =>
      t instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) && !t.closest('[role="dialog"]');
    const on = (e: FocusEvent) => setFieldFocused(isField(e.target));
    const off = () => setFieldFocused(false);
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); };
  }, []);

  const submitFeedback = trpc.siteFeedback.submit.useMutation();

  const reset = () => {
    setMessage("");
    setWantsToShareEmail(false);
    setEmail("");
    setFile(null);
    setSubmitted(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > MAX_ATTACHMENT_BYTES) {
      toast.error("That file is too large — attachments are limited to 5MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Add a message before sending.");
      return;
    }
    if (wantsToShareEmail && email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("That email address doesn't look right.");
      return;
    }

    try {
      let attachment: { filename: string; base64Content: string; contentType: string } | undefined;
      if (file) {
        const base64Content = await fileToBase64(file);
        attachment = {
          filename: file.name,
          base64Content,
          contentType: file.type || "application/octet-stream",
        };
      }

      await submitFeedback.mutateAsync({
        page: location,
        message: message.trim(),
        email: wantsToShareEmail && email.trim() ? email.trim() : undefined,
        attachment,
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      toast.error("Couldn't send that just now. Please try again in a moment.");
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Reset shortly after close so the form doesn't visibly flash back
      // to its default state while the closing animation is still playing.
      setTimeout(reset, 200);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className={`fixed right-5 bottom-5 z-40 rounded-full shadow-lg gap-2 px-4 py-5 max-sm:px-3 max-sm:py-3 max-sm:right-3 max-sm:bottom-3 transition-opacity ${fieldFocused ? "max-sm:opacity-0 max-sm:pointer-events-none" : ""}`}
        size="sm"
      >
        <MessageSquarePlus className="h-4 w-4" />
        {/* On a phone only the icon shows, so the button no longer covers a file row's controls. */}
        <span className="max-sm:sr-only">Feedback</span>
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
              <DialogTitle className="mb-1">Thanks for the feedback!</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {wantsToShareEmail && email.trim()
                  ? "We'll follow up by email once this is resolved."
                  : "Sent anonymously — we appreciate you taking the time."}
              </p>
              <Button className="mt-5" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Send feedback</DialogTitle>
                <DialogDescription>On this page: {location}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <Textarea
                  placeholder="What worked, what didn't, what would help?"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  className="min-h-[100px]"
                />

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setWantsToShareEmail(false)}
                    className={`text-sm font-medium rounded-lg border py-2 transition-colors ${
                      !wantsToShareEmail
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    Anonymous
                  </button>
                  <button
                    type="button"
                    onClick={() => setWantsToShareEmail(true)}
                    className={`text-sm font-medium rounded-lg border py-2 transition-colors ${
                      wantsToShareEmail
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    Include my email
                  </button>
                </div>

                {wantsToShareEmail && (
                  <div>
                    <Label htmlFor="feedback-email" className="text-xs mb-1.5 block">
                      We'll only use this to follow up on your feedback
                    </Label>
                    <Input
                      id="feedback-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  {file ? (
                    <div className="flex items-center justify-between text-sm border border-border rounded-lg px-3 py-2">
                      <span className="flex items-center gap-2 truncate">
                        <Paperclip className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{file.name}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="text-muted-foreground hover:text-foreground shrink-0 ml-2"
                        aria-label="Remove attachment"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center gap-2 text-sm text-muted-foreground border border-dashed border-border rounded-lg px-3 py-2.5 hover:border-primary/40 transition-colors"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      Attach a file (optional, 5MB max)
                    </button>
                  )}
                </div>

                <Button
                  className="w-full"
                  onClick={handleSubmit}
                  disabled={submitFeedback.isPending}
                >
                  {submitFeedback.isPending ? "Sending..." : "Submit feedback"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
